import ComponentWrapper, {
  ComponentInViewContext,
} from '../../fragments/component-wrapper/ComponentWrapper.jsx';
import IconUI from '../../fragments/icon/IconUI.jsx';
import Cinemagraph from '../../fragments/cinemagraph/Cinemagraph.jsx';
import CustomImage from '../../fragments/custom-image/CustomImage.jsx';
import TextScaleWidth from '../../fragments/text-scale-width/TextScaleWidth.jsx';

import AnimateSplitLines from '../../fragments/split-text/AnimateSplitLines.jsx';

import Button from '../../fragments/button/Button.jsx';
import { useVideoLightbox } from '../../context/VideoLightboxContext';

import { useState, useRef, useEffect, useContext } from 'react';
import {
  motion,
  useScroll,
  useTransform,
  useMotionValueEvent,
  useReducedMotion,
  cubicBezier,
  circOut,
  useSpring,
  easeIn,
  easeOut,
  easeInOut,
} from 'motion/react';

import { useCustomScroll } from '../../utils/useCustomScroll';
import { calculateDelay, CUSTOM_EASE } from '../../utils/animations';
import PageLoader from '../../fragments/page-loader/PageLoader.jsx';
import {
  EXPAND_START,
  HERO_DELAY,
  LOADER_TOTAL,
  NAV_RELEASE,
} from '../../fragments/page-loader/timeline';
import {
  useDimensions,
  useIsDesktop,
} from '../../context/ThemeContextManagement';

// ─── First-load page loader ───────────────────────────────────────────────────
// The loader is homepage-only, and this block is the homepage hero, so it owns
// the whole thing: it mounts the overlay, holds the scroll lock, holds the nav
// back, and delays its own two entrance animations to fit the loader timeline.
// Nothing outside this file (and PageLoader's own directory) knows the loader
// exists — every other page renders as if it were never built.
//
// First-load scale-up of the hero media: the whole page used to grow with the
// loader window, now only the media does. 0.73 start, and the same 1.5s
// duration as PageTransition's TRANSITION_DURATION, delayed to the loader's
// frame expand.
const LOADER_MEDIA_SCALE = 0.73;
const LOADER_MEDIA_DURATION = 1.5;

export default function HeroHome({ block, index, isInitialLoad = false }) {
  const { open } = useVideoLightbox();
  const dimensions = useDimensions();
  const isDesktop = useIsDesktop();

  const containerRef = useRef(null);
  const { scrollYProgress: scrollYProgressShort } = useCustomScroll({
    target: containerRef,
    offset: ['start start', 'end end'],
  });
  const { scrollYProgress: scrollYProgressLong } = useCustomScroll({
    target: containerRef,
    offset: ['start start', 'end start'],
  });

  const timeline1 = useTransform(scrollYProgressShort, [0, 0.5], [0, 1], {
    ease: easeInOut,
  });

  const timeline2 = useTransform(scrollYProgressShort, [0.5, 1], [0, 1], {
    // ease: easeOut,
    // ease: easeIn,
    // ease: cubicBezier(0.6, 0, 0.69, 0.22),
    // ease: easeIn,
  });

  // const scale = useTransform(scrollYProgressShort, [0, 1], [0.4, 0.4], {
  const y = useTransform(timeline2, [0.1, 1], ['0vh', '-100vh'], {
    // ease: easeOut,
    // ease: easeIn,
    // ease: cubicBezier(0.6, 0, 0.69, 0.22),
    ease: easeIn,
  });

  const createLetterTransform = (index) => {
    const staggerAmount = 0.06;
    // const startOffset = 0.05 + index * staggerAmount;
    const startOffset = index * staggerAmount;

    return useTransform(timeline2, [startOffset, 1], ['0vh', '-130vh'], {
      // ease: easeOut,
      // ease: easeIn,
      // ease: cubicBezier(0.6, 0, 0.69, 0.22),
      ease: easeIn,
    });
  };

  const scale = useTransform(timeline1, [0, 1], [1, 0.7]);
  const scaleImages = useTransform(timeline1, [0, 1], [1.2, 1]);

  // Eased 0→1 driver for the clip-path; kept separate from the px math below
  // so easing is handled by Motion's array interpolation, not recomputed
  // per-frame inside the transform function.
  const clipProgress = useTransform(timeline1, [0, 1], [0, 1], {
    ease: easeInOut,
  });

  // Horizontal inset matches the previous inset(25%) end state (same final
  // width); the vertical inset is derived from the live viewport size so the
  // final box is an exact 3:4 (w:h) rectangle instead of following whatever
  // aspect ratio the viewport happens to be.
  const clipPath = useTransform(clipProgress, (progress) => {
    const { screenWidth, lvhHeight } = dimensions;
    if (!screenWidth || !lvhHeight) return 'inset(0% 0%)';

    const sideInsetFinal = screenWidth * (isDesktop ? 0.23 : 0.05);
    const finalWidth = screenWidth - sideInsetFinal * 2;
    const finalHeight = (finalWidth * 9) / 16;
    const topInsetFinal = Math.max((lvhHeight - finalHeight) / 2, 0);

    return `inset(${topInsetFinal * progress}px ${sideInsetFinal * progress}px)`;
  });

  const overlayY = useTransform(timeline1, [0, 0.8], [0, 20]);
  const overlayOpacity = useTransform(timeline1, [0, 0.8], [1, 0]);

  // ─── heroOverlay clip-path load-in (mirrors CustomImage's reveal) ─────────
  const hasOverlayPlayed = useRef(false);
  const isOverlayInView = useContext(ComponentInViewContext) ?? true;
  const reduceOverlayMotion = useReducedMotion();

  useEffect(() => {
    if (isOverlayInView) hasOverlayPlayed.current = true;
  }, [isOverlayInView]);

  // Whether the loader plays for this render. Deliberately free of any
  // `typeof window` / matchMedia guard — it decides markup that is baked into
  // the prerendered HTML (the overlay itself, and Motion `initial` values,
  // which are locked in at mount), so it has to resolve the same at build time
  // and on the client's first render. `isInitialLoad` comes from _app.js via
  // BlockLayout and is only ever true for a hard load, never a SPA nav.
  const showLoader = isInitialLoad && index === 0;
  // Reduced motion is gated separately, on the animations only: PageLoader
  // bails out of its own timeline, and _pageLoader.scss hides the overlay
  // outright, so the markup can stay identical either way.
  const loaderActive = showLoader && !reduceOverlayMotion;

  useEffect(() => {
    // matchMedia rather than `loaderActive`: this effect runs once, so it must
    // not capture a useReducedMotion() value that hadn't settled on the very
    // first render.
    if (!showLoader) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    // Lenis is created in _app.js's effect, which runs *after* this one (child
    // effects fire first), so window.lenis doesn't exist yet — take the lock on
    // the next frame instead. PageTransition no longer calls start() on a first
    // load, so nothing fights us for it.
    const raf = requestAnimationFrame(() => window.lenis?.stop());

    // Holds the nav at opacity 0 while the overlay covers the page, then lets
    // it fade in over the expand — see the .is-loading rules in
    // NavigationDesktop.scss / NavigationMobile.scss. Previously done by
    // PageTransition holding body.is-transitioning open for the loader window.
    document.body.classList.add('is-loading');

    const timers = [
      setTimeout(
        () => document.body.classList.remove('is-loading'),
        NAV_RELEASE * 1000
      ),
      setTimeout(() => window.lenis?.start(), LOADER_TOTAL * 1000 + 100),
    ];

    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      document.body.classList.remove('is-loading');
      window.lenis?.start();
    };
  }, []);

  // The loader timeline is tuned so the heading reveal is mid-animation when the
  // page-peek window opens, rather than finishing invisibly behind the overlay.
  // The hr and footer are staggered off this same base so the three read as one
  // continuous motion.
  const overlayBaseDelay =
    calculateDelay(1, index) + (loaderActive ? HERO_DELAY : 0);

  const headingRevealVariants = {
    // line-height is 0.765, so glyph ink overflows the line box — the resting
    // insets are relaxed past the edges so ascenders/descenders aren't shaved.

    // rotate: '5deg',
    hidden: {
      clipPath: 'inset(-30% 0% 100% 0%)',
      y: '80%',
      transformOrigin: 'top left',
    },
    visible: {
      transformOrigin: 'top left',
      clipPath: 'inset(-30% 0% -15% 0%)',
      y: '0%',

      transition: {
        delay: overlayBaseDelay,
        duration: 1.2,
        ease: cubicBezier(...CUSTOM_EASE),
      },
    },
  };

  const ruleRevealVariants = {
    hidden: { scaleX: 0 },
    visible: {
      scaleX: 1,
      transition: {
        delay: overlayBaseDelay + 0.6,
        duration: 0.9,
        ease: cubicBezier(...CUSTOM_EASE),
      },
    },
  };

  // Split across two elements so the mask stays put while the content slides
  // down through it — the same wrapper/inner split AnimateSplitLines uses. A
  // top-down mask plus a -100% y start makes the footer read as emerging from
  // the hr line above it.
  const footerMaskVariants = {
    hidden: { clipPath: 'inset(0% 0% 100% 0%)' },
    visible: {
      clipPath: 'inset(0% 0% 0% 0%)',
      transition: {
        delay: overlayBaseDelay + 0.9,
        duration: 0.8,
        ease: cubicBezier(...CUSTOM_EASE),
      },
    },
  };

  const footerSlideVariants = {
    hidden: { y: '-100%' },
    visible: {
      y: '0%',
      transition: {
        delay: overlayBaseDelay + 0.9,
        duration: 0.8,
        ease: cubicBezier(...CUSTOM_EASE),
      },
    },
  };

  const makeRevealProps = (variants) =>
    !reduceOverlayMotion
      ? {
          variants,
          initial: hasOverlayPlayed.current ? false : 'hidden',
          animate: isOverlayInView ? 'visible' : 'hidden',
        }
      : {};

  // Applied to the media wrapper rather than the scroll-driven wrapper above it,
  // so this one-shot load-in and the scroll scale stay independent motion values
  // (they simply multiply, and this one has settled at 1 before Lenis resumes).
  const mediaScaleProps = loaderActive
    ? {
        initial: { scale: LOADER_MEDIA_SCALE },
        animate: { scale: 1 },
        transition: {
          delay: EXPAND_START,
          duration: LOADER_MEDIA_DURATION,
          ease: cubicBezier(...CUSTOM_EASE),
        },
      }
    : {};

  const [buttonActive, setButtonActive] = useState(false);
  useMotionValueEvent(timeline1, 'change', (latest) => {
    setButtonActive(latest > 0.5);
  });

  const scaleTextBackground = useTransform(
    scrollYProgressLong,
    [0, 1],
    [1, 0.9]
    // {
    //   ease: easeInOut,
    // }
  );

  // const offset = useTransform(
  //   scrollYProgress,
  //   [0, 1],
  //   [isDesktop ? 150 : 50, 0]
  // );

  const pictures = [
    {
      src: block?.image,
      scale: 1,
    },
    {
      src: block?.image,
      scale: 1,
    },
    {
      src: block?.image,
      scale: 1,
    },
  ];

  return (
    <ComponentWrapper className="HeroHome" padding={false}>
      {showLoader && <PageLoader />}
      <div ref={containerRef}>
        <div className="HeroHome-inner">
          {block.subheading && (
            <div className="HeroHome-textContainer">
              <div className={`HeroHome-textItem`}>
                <motion.div
                  style={{ scale: scaleTextBackground, width: '100%' }}
                >
                  <TextScaleWidth text={block.subheading} />
                </motion.div>
              </div>
            </div>
          )}
          <motion.div
            style={{ scale: scaleImages }}
            className={'HeroHome-grid'}
          >
            {block.images.map((image, index) => {
              const imageY = createLetterTransform(index);
              return (
                <motion.div
                  key={index}
                  style={{ y: imageY }}
                  className={'HeroHome-el'}
                >
                  <div className={'HeroHome-imageContainer'}>
                    {/* No forceLoad: these three are decorative floaters at
                        15vw. `priority` made Next preload all three at high
                        priority, ahead of the hero's own poster — which is the
                        actual LCP element. */}
                    <CustomImage
                      className={`HeroHome-img`}
                      image={image}
                      fill={true}
                      desktopWidth={15}
                      mobileWidth={33}
                    />
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
          <motion.div
            style={{
              zIndex: '1',
              y,
            }}
            className="HeroHome-hero"
          >
            {block.video && (
              <div
                className={`HeroHome-buttonOverlay dark${buttonActive ? ' active' : ''}`}
                aria-hidden={buttonActive ? undefined : 'true'}
              >
                <Button
                  buttonLabel="Watch now"
                  icon="play"
                  size="large"
                  onClick={() => open(block.video, block?.image?.sourceUrl)}
                  disabled={!buttonActive}
                  tabIndex={buttonActive ? undefined : -1}
                />
              </div>
            )}
            <motion.div
              style={{ scale, clipPath, position: 'absolute', inset: 0 }}
            >
              <motion.div className="HeroHome-darkOverlay"></motion.div>
              <motion.div
                style={{ opacity: overlayOpacity, y: overlayY }}
                className="HeroHome-heroOverlay dark"
              >
                {block.heading && (
                  <motion.div
                    style={{ willChange: 'clip-path' }}
                    {...makeRevealProps(headingRevealVariants)}
                  >
                    <TextScaleWidth
                      text={block.heading}
                      as={index === 0 ? 'h1' : 'h2'}
                    />
                  </motion.div>
                )}
                <motion.hr {...makeRevealProps(ruleRevealVariants)} />
                <motion.div
                  className="HeroHome-heroFooterMask"
                  {...makeRevealProps(footerMaskVariants)}
                >
                  <motion.div {...makeRevealProps(footerSlideVariants)}>
                    <div className="HeroHome-heroFooter">
                      <IconUI icon={'arrowDown'} />
                      <p className="h7">Scroll to explore</p>
                    </div>
                  </motion.div>
                </motion.div>
              </motion.div>
              <motion.div className="HeroHome-heroInner" {...mediaScaleProps}>
                {block.videoBackground ? (
                  <Cinemagraph
                    media={block.videoBackground}
                    // The poster is the hero's real LCP candidate: an HLS
                    // manifest can't be preloaded or fetchpriority'd, so
                    // without this the browser has nothing to paint until the
                    // first video segment lands. Cinemagraph fades it out on
                    // `playing` and keeps it as the static fallback when
                    // autoplay is blocked.
                    poster={block?.image}
                    fetchPriority={index === 0 ? 'high' : undefined}
                    fill={true}
                    forceLoad={true}
                    parallax={true}
                  />
                ) : (
                  <CustomImage
                    className={`HeroHome-heroImage`}
                    image={block?.image}
                    fill={true}
                    forceLoad={true}
                    quality={90}
                    desktopWidth={100}
                  />
                )}
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        <div className="HeroHome-spacer" />
      </div>

      <div className={'l-grid'}>
        <div className="l-grid__col l-grid__col--l-8-12 l-grid__col--l-push-2-12">
          {block.description && (
            // <p className="HeroHome-description h3">{block.description}</p>
            <p className="HeroHome-description h3">
              {' '}
              <AnimateSplitLines html={block.description} />
            </p>
          )}
        </div>
      </div>
    </ComponentWrapper>
  );
}
